import { createRefPath } from "global/firestoreData/appData/appRefPaths";
import _ from "lodash";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppTimestampContext } from "../AppTimestampContext";
import { DatabaseContext } from "../DatabaseContext";
import { _showAppLogs, LogContext } from "../LogContext";
import { PapsContext } from "../PapsContext";
import { useCacheDataManager } from "./cacheDataManager";
import { _cacheGroupTypes } from "./useTimestamp";

const firestoreSettingsCallTypes = {
  settings: 'settings',
  settingDocs: 'settingDocs',
};

export const useAppSettings = () => {

  const { paps_state } = useContext(PapsContext);
  const { pathViews } = paps_state ?? {};

  const homeSettings = useHomeSettings();
  const clientSettings = useClientSettings(pathViews?.clients);
  const eventSettings = useEventSettings(pathViews?.events);

  const combinedSettings = useMemo(() => ({
    home: homeSettings.settings,
    client: clientSettings.settings,
    event: eventSettings.settings,
  }), [homeSettings.settings, clientSettings.settings, eventSettings.settings]);

  return {
    settings: combinedSettings,
    loading: homeSettings.loading || clientSettings.loading || eventSettings.loading,
    fetched: homeSettings.fetched && clientSettings.fetched && eventSettings.fetched
  };

}

const useFirestoreSettings = ({ layerName, settingsKey }) => {

  // logContext
  const logContext = useContext(LogContext)
  const { log_handlers } = logContext ?? {}

  const appTimestampContext = useContext(AppTimestampContext);
  const { appTimestamp_state } = appTimestampContext ?? {}
  const { timestampInfo } = appTimestamp_state ?? {};
  const { timestamps, timestampsFetched } = timestampInfo ?? {};
  const _timestamp_db = timestamps?.[_cacheGroupTypes.settings]?.[settingsKey];

  const { database_fns } = useContext(DatabaseContext) ?? {};

  const cdm = useCacheDataManager({ fsdb: database_fns })

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetched, setFetched] = useState(false);

  const unsubRef = useRef(null);
  const prevDataRef = useRef(null);

  useEffect(() => {

    if (!timestampsFetched) return;

    // Clean up previous listener
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }

    const init = async () => {

      setLoading(true);
      _showAppLogs.dbFetch && console.log('🕒 _timestamp_db', settingsKey, _timestamp_db)

      const cache_groupType = _cacheGroupTypes.settings;
      const cached = await cdm.getSubItem(cache_groupType, settingsKey);
      const _timestamp_cache = cached?.settingsDoc?._timestamp;

      // if the timestamps are the same, return the cached settings
      if (_timestamp_db && _timestamp_cache && _timestamp_db === _timestamp_cache) {
        _showAppLogs.settings && log_handlers.addLog(`♻️⚙️ Loaded settings (${settingsKey}) from cache`);
        setSettings(cached.settingsDocs);
        setLoading(false);
        setFetched(true)
        return;
      }

      // only gets to this point if no settings are in cache
      const results = await Promise.all([
        fetchAndGet(database_fns, "settingsDoc", layerName, settingsKey, getConfig_settings, log_handlers),
        fetchAndGet(database_fns, "settingsDocs", layerName, settingsKey, getConfig_settingsDocs, log_handlers),
      ].filter(Boolean));

      const mergedData = _(results)
        .compact()
        .fromPairs()
        .pickBy((v) => !_.isNil(v) && !_.isEmpty(v))
        .value();

      if (settings && mergedData?.settingsDocs) {
        _.forEach(settings, (setting, sk) => {
          if (!mergedData.settingsDocs[sk]) {
            mergedData.settingsDocs[sk] = setting
          }
        })
      }

      const ts_update = mergedData?.settingsDoc?._timestamp ?? Date.now();
      await cdm.mergeToGroup(cache_groupType, settingsKey, mergedData, ts_update);

      prevDataRef.current = mergedData;
      setSettings(mergedData?.settingsDocs);
      setLoading(false);
      setFetched(true)
    };

    if (settingsKey) {
      init();
    } else {
      setSettings({})
      setLoading(false);
      setFetched(true)
    }

    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [_timestamp_db, timestampsFetched, layerName, settingsKey]); // ensures stable comparison

  return { settings, loading, fetched };
};

// 🔹 Helpers
const getConfig_settings = (settingsKey, callback) => ({
  refPath: createRefPath(["settings", settingsKey]),
  callback,
  opts: { ignoreId: false, returnFirstObject: true, listen: true },
});

const getConfig_settingsDocs = (settingsKey, callback) => ({
  refPath: createRefPath(["settings", settingsKey, "settings"]),
  callback,
  opts: { ignoreId: true, listen: true },
});

const fetchAndGet = async (database_fns, subKey, layerName, settingsKey, fetchConfigFn, log_handlers) => {
  return new Promise((resolve, reject) => {
    const callback = (data) => {
      _showAppLogs.dbFetch && log_handlers.addLog(`🌐⚙️ Fetched ${subKey} settings(${layerName}/${settingsKey})`);
      resolve([subKey, _.cloneDeep(data)]);
    };
    try {
      database_fns.get_data(fetchConfigFn(settingsKey, callback));
    } catch (err) {
      reject(err);
    }
  });
};

const useHomeSettings = () => {
  return useFirestoreSettings({ layerName: 'home', settingsKey: 'home' });
};

const useClientSettings = (settingsKey) => {
  return useFirestoreSettings({ layerName: 'clients', settingsKey });
};

const useEventSettings = (settingsKey) => {
  return useFirestoreSettings({ layerName: 'events', settingsKey });
};